import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Hero from '../components/Hero';
import Plans from '../components/Plans';
import AboutUs from '../components/AboutUs';
import ContactUs from '../components/ContactUs';
import '../styles/Home.css';

const STATS = [
  { number: '16M', label: { en: 'Inhabitants', fr: 'Habitants' } },
  { number: '2', label: { en: 'Continents', fr: 'Continents' } },
  { number: '4,000+', label: { en: 'Grand Bazaar Shops', fr: 'Boutiques au Grand Bazar' } },
  { number: '30', label: { en: 'Expert Guides', fr: 'Guides experts' } },
];

const WHY_ITEMS = [
  { icon: '🏛️', tKey: 'why_jobs' },
  { icon: '🛍️', tKey: 'why_education' },
  { icon: '✈️', tKey: 'why_safety' },
  { icon: '🕌', tKey: 'why_culture' },
];

const PROCESS_STEPS = [
  { step: '01', num: 1 },
  { step: '02', num: 2 },
  { step: '03', num: 3 },
  { step: '04', num: 4 },
];

const PATHWAYS = [
  { icon: '🛂', num: 1, color: '#1a56db' },
  { icon: '🏛️', num: 2, color: '#0e9f6e' },
  { icon: '⛴️', num: 3, color: '#7e3af2' },
  { icon: '🛍️', num: 4, color: '#e3a008' },
  { icon: '🏥', num: 5, color: '#f05252' },
  { icon: '📦', num: 6, color: '#3f83f8' },
];

const TESTIMONIALS = [
  { initials: '⛴️', tKey: 'testi1' },
  { initials: '💳', tKey: 'testi2' },
  { initials: '🏛️', tKey: 'testi3' },
];

const FAQ_NUMS = [1, 2, 3, 4, 5];

const Home = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'en';

  return (
    <div className="home-page">
      <Hero />

      {/* Stats Strip */}
      <section className="stats-strip">
        <div className="stats-inner">
          {STATS.map((s, i) => (
            <div key={i} className="stat-item">
              <div className="stat-number">{s.number}</div>
              <div className="stat-label">{s.label[lang] || s.label.en}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Istanbul */}
      <section className="why-section">
        <div className="section-inner">
          <div className="section-header">
            <span className="home-badge">{t('why_us_badge')}</span>
            <h2>{t('why_us_title')}</h2>
            <p>{t('why_us_desc')}</p>
          </div>
          <div className="why-grid">
            {WHY_ITEMS.map((item, i) => (
              <div key={i} className="why-card">
                <div className="why-icon">{item.icon}</div>
                <h3>{t(`${item.tKey}_title`)}</h3>
                <p>{t(`${item.tKey}_sub`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="process-section">
        <div className="section-inner">
          <div className="section-header light">
            <span className="home-badge light">{t('process_badge')}</span>
            <h2>{t('process_title')}</h2>
            <p>{t('process_sub')}</p>
          </div>
          <div className="process-steps">
            {PROCESS_STEPS.map((item, i) => (
              <div key={i} className="process-step">
                <div className="process-step-num">{item.step}</div>
                <h4>{t(`step${item.num}_title`)}</h4>
                <p>{t(`step${item.num}_desc`)}</p>
                {i < PROCESS_STEPS.length - 1 && <div className="process-connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trip Pathways */}
      <section className="pathways-section">
        <div className="section-inner">
          <div className="section-header">
            <span className="home-badge">{t('pathways_badge')}</span>
            <h2>{t('pathways_title')}</h2>
            <p>{t('pathways_sub')}</p>
          </div>
          <div className="pathways-grid">
            {PATHWAYS.map((p, i) => (
              <div key={i} className="pathway-card" style={{ '--pw-color': p.color }}>
                <div className="pathway-icon">{p.icon}</div>
                <h4>{t(`pathway${p.num}_name`)}</h4>
                <p>{t(`pathway${p.num}_desc`)}</p>
                <span className="pathway-arrow">→</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <Plans />

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="section-inner">
          <div className="section-header">
            <span className="home-badge">{t('testi_badge')}</span>
            <h2>{t('testi_title')}</h2>
            <p>{t('testi_sub')}</p>
          </div>
          <div className="testimonials-grid">
            {TESTIMONIALS.map((tm, i) => (
              <div key={i} className="testimonial-card">
                <div className="tm-stars">💡</div>
                <p className="tm-text">"{t(`${tm.tKey}_text`)}"</p>
                <div className="tm-author">
                  <div className="tm-avatar">{tm.initials}</div>
                  <div>
                    <div className="tm-name">{t(`${tm.tKey}_name`)}</div>
                    <div className="tm-country">{t(`${tm.tKey}_country`)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="section-inner">
          <div className="section-header">
            <span className="home-badge">{t('faq_badge')}</span>
            <h2>{t('faq_title')}</h2>
          </div>
          <div className="faq-list">
            {FAQ_NUMS.map((n) => (
              <FaqItem key={n} question={t(`faq${n}_q`)} answer={t(`faq${n}_a`)} />
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <AboutUs />

      {/* Contact */}
      <ContactUs />

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-inner">
          <h2>{t('cta_title')}</h2>
          <p>{t('cta_sub')}</p>
          <div className="cta-btns">
            <button className="btn-primary" onClick={() => navigate('/plans')}>{t('cta_primary')}</button>
            <button className="btn-secondary" onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}>{t('cta_secondary')}</button>
          </div>
        </div>
      </section>
    </div>
  );
};

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="faq-question">
        <span>{question}</span>
        <span className="faq-chevron">{open ? '▲' : '▼'}</span>
      </div>
      {open && <div className="faq-answer">{answer}</div>}
    </div>
  );
};

export default Home;
